import { ArraySchema, BooleanSchema, StringSchema } from 'yup'

import yup from 'civic-champs-shared/utils/yup'
import { QuestionType, isAnswerQuestionType } from 'civic-champs-shared/question-sets/types'
import { PHONE_QUESTION_TARGETS, EMAIL_QUESTION_TARGETS } from 'civic-champs-shared/question-sets/question-target'
import { allowOptions } from 'question-sets/components/NewQuestionItemEditor'

const hasRichTextContent = (value?: string) => {
  if (!value) {
    return false
  }

  return (
    value
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim().length > 0
  )
}

const allowedTargetsByType: Partial<Record<QuestionType, string[]>> = {
  [QuestionType.PHONE_NUMBER]: [...PHONE_QUESTION_TARGETS, ''],
  [QuestionType.EMAIL]: [...EMAIL_QUESTION_TARGETS, ''],
}

export const questionSchema = (isQuestionnaireAsSurveyEnabled: boolean) =>
  yup.object({
    questionType: yup
      .string()
      .oneOf([
        QuestionType.LONG_TEXT,
        QuestionType.SHORT_TEXT,
        QuestionType.SINGLE_CHOICE,
        QuestionType.MULTIPLE_CHOICE,
        QuestionType.DROPDOWN,
        QuestionType.NUMBER,
        QuestionType.DATE_OF_BIRTH,
        QuestionType.PHONE_NUMBER,
        QuestionType.EMAIL,
        QuestionType.DOCUMENT,
        QuestionType.TEXT_BLOCK,
        ...(isQuestionnaireAsSurveyEnabled ? [QuestionType.HOME_ADDRESS, QuestionType.EMERGENCY_CONTACT] : []),
      ])
      .required(),
    target: yup
      .string()
      .nullable()
      .when('questionType', (questionType: QuestionType, schema: yup.StringSchema) => {
        const allowedTargets = allowedTargetsByType[questionType]
        if (allowedTargets) {
          return schema.oneOf(allowedTargets).notRequired()
        }
        return schema.notRequired()
      }),
    question: yup.string().when('questionType', {
      is: QuestionType.TEXT_BLOCK,
      then: (schema: StringSchema) =>
        schema
          .max(5000)
          .required('${label} is required')
          .test('rich-text-content', '${label} is required', (value?: string) => hasRichTextContent(value))
          .label('question'),
      otherwise: (schema: StringSchema) => schema.max(5000).required('${label} is required').trim().label('question'),
    }),
    isRequired: yup.boolean().when('questionType', {
      is: QuestionType.TEXT_BLOCK,
      then: (schema: BooleanSchema) =>
        schema.transform(() => false).oneOf([false], 'Text Block questions cannot be marked as required'),
      otherwise: (schema: BooleanSchema) => schema.required(),
    }),
    options: yup.array().when('questionType', (questionType: QuestionType, schema: ArraySchema<string>) => {
      if (allowOptions(questionType)) {
        const maxLength = questionType === QuestionType.DROPDOWN ? 50 : 1000
        return schema
          .of(
            yup.object({
              value: yup.string().min(1).max(maxLength).required('${label} is required').label('answer'),
              meta: yup.mixed(),
            }),
          )
          .min([QuestionType.DOCUMENT, QuestionType.MULTIPLE_CHOICE].includes(questionType) ? 1 : 2)
          .required()
      } else {
        return schema.notRequired()
      }
    }),
  })

export const questionSetSchema = (isQuestionnaireAsSurveyEnabled: boolean) =>
  yup.object({
    name: yup.string().trim().required(),
    questions: yup
      .array()
      .of(questionSchema(isQuestionnaireAsSurveyEnabled))
      .min(1)
      .test(
        'hasAnswerQuestion',
        'Questionnaire must contain at least one answer-type question',
        (questions: Array<{ questionType?: QuestionType }> | undefined) =>
          !!questions?.some(q => isAnswerQuestionType(q.questionType)),
      )
      .required(),
  })
